// @flow
import {observable, action, computed} from 'mobx'
import {Store} from 'Store'

const separate = ['MACD', 'RSI', 'ATR', 'OBV', 'SHORT_INTEREST', 'PRICE_TO_BOOK_VALUE', 'PRICE_TO_EARNINGS', 'PRICE_TO_REVENUE', 'SENTIMENT']

export class Indicator {
  $key: string;
  $value: any;
  root: Store
  
  @observable ident: string
  @observable type: string
  @observable separate: boolean

  @observable loaded = false
  @observable loading = false
  @observable visible = true

  @observable params = {}
  @observable paramsToChange = {}
  @observable data = {}

  constructor(rootStore: Store, ident: string, type: string) {
    this.root = rootStore

    this.ident = ident
    this.type = type
    this.separate = separate.includes(ident)
  }

  @computed get options() {
    const {analysis} = this.root
    const obj = analysis.supportedParts.find(row => row.ident === this.ident)
    return obj.params.filter(row => row.ident !== 'ticker' && row.ident !== 'MACD_TYPE')
  }

  @computed get hasParams() {
    return Object.keys(this.params).length > 0 && this.params.constructor === Object
  }

  @computed get name() {
    const {analysis} = this.root
    const {name} = analysis.supportedParts.find(row => row.ident === this.ident)
    return name || this.ident
  }

  @computed get syntax_parts(): {}[] {
    if (this.ident === 'BB') {
      return(
        ['upper', 'middle', 'lower'].map(type => ({
          ident: this.ident,
          params: {BOLLINGER_TYPE: type, ...this.params}
        }))
      )
    }
    if (this.ident === 'MACD') {
      return(
        ['', 'signal', 'hist'].map(type => ({
          ident: this.ident,
          params: {MACD_TYPE: type, ...this.params}
        }))
      )
    }
    return [{ident:this.ident,params:{...this.params}}]
  }

  @computed get syntax() {
    let syntax = this.ident.toLowerCase()
    if (syntax === '52_week_high') {
      syntax = 'h52'
    }
    if (syntax === '52_week_low') {
      syntax = 'l52'
    }
    return syntax
  }

  @action change(key: string, value: any) {
    this[key] = value
  }

  @action changeParam = (name: string, value: string) => {
    this.paramsToChange[name] = `${value}`
  }

  @action remove = () => {
    this.root.analysis.removeIndicator(this.type, this.ident)
  }

  @action commitChanges = () => {
    this.params = Object.assign({}, this.paramsToChange)
    this.load()
  }

  @action discardChanges = () => {
    this.paramsToChange = Object.assign({}, this.params)
  }

  @action toggleVisibility = () => {
    this.visible = !this.visible
  }

  @action async load() {
    this.loading = true
    this.loaded = false
    const {analysis} = this.root
    const {start_dt, end_dt, bar_size, ticker} = analysis
    let body = {reports:[{
      start_dt: start_dt.toISOString(),
      end_dt: end_dt.toISOString(),
      bar_size: bar_size,
      tickers: [ticker],
      syntax_parts: this.syntax_parts
    }]}

    try {
      const resObj = await analysis.prepareReport(body)
      if (this.ident === 'BB') {
        const ts = resObj.reports[0].syntax_part_reports_data.find(row => row.label.indexOf('upper') !== -1).data.ts
        const upper = resObj.reports[0].syntax_part_reports_data.find(row => row.label.indexOf('upper') !== -1).data.values
        const middle = resObj.reports[0].syntax_part_reports_data.find(row => row.label.indexOf('middle') !== -1).data.values
        const lower = resObj.reports[0].syntax_part_reports_data.find(row => row.label.indexOf('lower') !== -1).data.values

        let data = {}
        for (let i=0; i < upper.length; i++) {
          data[ts[i]] = {
            bb: {
              top: upper[i],
              middle: middle[i],
              bottom: lower[i]
            }
          }
        }
        this.data = data
      } else if (this.ident === 'MACD') {
        const ts = resObj.reports[0].syntax_part_reports_data.find(row => row.label.endsWith('MACD')).data.ts
        const macd = resObj.reports[0].syntax_part_reports_data.find(row => row.label.endsWith('MACD')).data.values
        const signal = resObj.reports[0].syntax_part_reports_data.find(row => row.label.endsWith('signal')).data.values
        const hist = resObj.reports[0].syntax_part_reports_data.find(row => row.label.endsWith('hist')).data.values

        let data = {}
        for (let i=0; i < macd.length; i++) {
          data[ts[i]] = {
            macd: {
              macd: macd[i],
              signal: signal[i],
              divergence: hist[i]
            }
          }
        }
        this.data = data
      } else {
        resObj.reports[0].syntax_part_reports_data.map(row => {
          this.data = analysis.processTickerData(row)
        })
      }
      analysis.calculateChartData()
    } finally {
      this.loaded = true
      this.loading = false
    }
  }
}
